import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';

import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { ProcessoVendaService } from '@shared/services/apis/vendas/processo-venda.service';
import { CompartilharVendaRequest } from '@shared/services/requests/vendas/compartilhar-venda.request';

export interface VdCompartilharDialogData {
    idVenda: number;
    /** Ex.: "Pedido de Venda nº 3" (documento não salvo não chega aqui: o shell recusa antes). */
    nomeDocumento: string;
    nomeEmpresa: string;
    linkPublicoToken: string | null;
    linkPublicoExpiracao: string | null;
    /** URL que o orquestrador montou para o link vigente; a tela nunca a monta. */
    urlLinkPublico: string | null;
    emailCliente: string | null;
}

/** O estado do link ao fechar, para o shell atualizar o documento sem reler tudo. */
export interface VdCompartilharDialogResult {
    linkPublicoToken: string | null;
    linkPublicoExpiracao: string | null;
    urlLinkPublico: string | null;
}

/**
 * Compartilhar por link (§10.4). Não existe anexo de PDF: o e-mail leva a URL, que renderiza a
 * versão vigente do documento. Um único link vigente por documento (§10.4.1): enviar com link
 * vigente gera um novo e revoga o anterior (confirmação inline antes), e "Revogar link" encerra
 * o acesso de quem já recebeu.
 *
 * O diálogo não fecha ao enviar, para o usuário poder copiar o link. Ao fechar, devolve o estado
 * atual do link se algo mudou; sem mudança devolve `undefined`.
 */
@Component({
    selector: 'app-vd-compartilhar-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatDialogModule,
        MatButtonModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatProgressSpinnerModule,
        MatSelectModule,
        AlertCardComponent,
    ],
    templateUrl: './vd-compartilhar-dialog.component.html',
    styleUrls: ['./vd-compartilhar-dialog.component.css'],
})
export class VdCompartilharDialogComponent {
    public readonly opcoesValidade: number[] = [7, 15, 30];

    public email: string;
    public mensagem: string;
    public assunto = '';
    public nomeDestinatario = '';
    public validadeDias = 7;
    public mostrarAvancado = false;

    public linkPublicoToken: string | null;
    public linkPublicoExpiracao: string | null;
    public urlLinkPublico: string | null;

    public confirmandoRevogacao = false;
    public confirmandoEnvio = false;
    public isEnviando = false;
    public isRevogando = false;

    private alterou = false;

    constructor(
        public dialogRef: MatDialogRef<VdCompartilharDialogComponent, VdCompartilharDialogResult>,
        @Inject(MAT_DIALOG_DATA) public data: VdCompartilharDialogData,
        private processoVendaService: ProcessoVendaService,
        private alertService: AlertService,
    ) {
        this.email = data.emailCliente ?? '';
        this.linkPublicoToken = data.linkPublicoToken;
        this.linkPublicoExpiracao = data.linkPublicoExpiracao;
        this.urlLinkPublico = data.urlLinkPublico;
        this.mensagem =
            `Olá! Segue o link do ${data.nomeDocumento.toLowerCase()} da ${data.nomeEmpresa} para sua análise. ` +
            'Qualquer dúvida, estou à disposição.';
    }

    /** Assunto que o orquestrador usa quando o campo fica vazio. */
    public get assuntoPadrao(): string {
        return `${this.data.nomeDocumento} — ${this.data.nomeEmpresa}`;
    }

    public get temLink(): boolean {
        return !!this.linkPublicoToken;
    }

    /** Vigente = token presente e expiração futura (mesma regra do servidor). */
    public get isLinkVigente(): boolean {
        return this.temLink && !!this.linkPublicoExpiracao && new Date(this.linkPublicoExpiracao).getTime() > Date.now();
    }

    public get isLinkVencido(): boolean {
        return this.temLink && !this.isLinkVigente;
    }

    public alternarAvancado(): void {
        this.mostrarAvancado = !this.mostrarAvancado;
    }

    public copiarLink(): void {
        if (!this.isLinkVigente) return;

        navigator.clipboard.writeText(this.urlLinkPublico ?? '').then(
            () => this.alertService.show('Link copiado', `Válido até ${this.formatarExpiracao()}.`, AlertType.success),
            () => this.alertService.show('Não foi possível copiar', 'Copie o link manualmente.', AlertType.warning),
        );
    }

    public pedirRevogacao(): void {
        this.confirmandoEnvio = false;
        this.confirmandoRevogacao = true;
    }

    public cancelarRevogacao(): void {
        this.confirmandoRevogacao = false;
    }

    public revogar(): void {
        if (this.isRevogando) return;

        this.isRevogando = true;

        this.processoVendaService.revogarLinkPublico(this.data.idVenda).subscribe({
            next: (res) => {
                this.isRevogando = false;

                if (res.isError) {
                    this.alertService.show('Não foi possível revogar', res.errorDescription ?? '', AlertType.warning);
                    return;
                }

                this.confirmandoRevogacao = false;
                this.linkPublicoToken = null;
                this.linkPublicoExpiracao = null;
                this.urlLinkPublico = null;
                this.alterou = true;
                this.alertService.show('Link revogado', 'A URL deixou de dar acesso ao documento.', AlertType.success);
            },
            error: (err) => {
                this.isRevogando = false;
                this.alertService.show('Erro', err.message, AlertType.error);
            },
        });
    }

    /** Valida o e-mail; com link vigente, pede a confirmação inline antes de gerar o novo. */
    public enviar(): void {
        if (this.isEnviando) return;

        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(this.email.trim())) {
            this.alertService.show('E-mail inválido', 'Informe o e-mail do destinatário para enviar o documento.', AlertType.warning);
            return;
        }

        if (this.isLinkVigente && !this.confirmandoEnvio) {
            this.confirmandoRevogacao = false;
            this.confirmandoEnvio = true;
            return;
        }

        this.confirmandoEnvio = false;
        this.executarEnvio();
    }

    public cancelarConfirmacaoEnvio(): void {
        this.confirmandoEnvio = false;
    }

    public fechar(): void {
        this.dialogRef.close(
            this.alterou
                ? { linkPublicoToken: this.linkPublicoToken, linkPublicoExpiracao: this.linkPublicoExpiracao, urlLinkPublico: this.urlLinkPublico }
                : undefined,
        );
    }

    private executarEnvio(): void {
        const email = this.email.trim();

        const request = new CompartilharVendaRequest();
        request.validadeDias = this.validadeDias;
        request.email = email;
        request.nomeDestinatario = this.nomeDestinatario.trim() || null;
        request.assunto = this.assunto.trim() || null;
        request.mensagem = this.mensagem.trim();

        this.isEnviando = true;

        this.processoVendaService.compartilhar(this.data.idVenda, request).subscribe({
            next: (res) => {
                this.isEnviando = false;

                // Link gerado mas e-mail falhou: a resposta vem com erro e o token preenchido; o link novo já vale.
                if (res.token) {
                    this.linkPublicoToken = res.token;
                    this.linkPublicoExpiracao = res.expiracao;
                    this.urlLinkPublico = res.url;
                    this.alterou = true;
                }

                if (res.isError) {
                    this.alertService.show('Não foi possível enviar', res.errorDescription ?? '', AlertType.warning);
                    return;
                }

                this.alertService.show('Enviado', `${this.data.nomeDocumento} enviado para ${email}.`, AlertType.success);
            },
            error: (err) => {
                this.isEnviando = false;
                this.alertService.show('Erro', err.message, AlertType.error);
            },
        });
    }

    private formatarExpiracao(): string {
        if (!this.linkPublicoExpiracao) return '';

        return new Date(this.linkPublicoExpiracao).toLocaleDateString('pt-BR');
    }
}
